import type { Vec2, WallGrid, WallMetrics } from '../types';
import type { ShapeDefinition } from './registry';
import { wallBoundsFor } from './registry';

/**
 * Equilateral triangles, alternating point-up and point-down along each row.
 *
 * This is the shape the `orientation` hook exists for: a triangular tiling
 * cannot be made from one orientation, so every odd column is the same panel
 * turned through 180°. The mesh is still emitted point-up in its own frame, so
 * a printed part always comes out in its canonical orientation and the rotation
 * lives in the wall placement instead.
 *
 * Triangles are also the reason `outline` receives the mounting gap. Squares
 * and hexagons realise a gap by spreading their lattice, but the two triangles
 * of a row separate along a different axis than two rows do, so scaling the
 * lattice would open uneven gaps. Shrinking the footprint instead gives exactly
 * the requested clearance on all three edges.
 */

const SQRT3 = Math.sqrt(3);

/** Edge length after the mounting gap has been taken out of the footprint. */
function insetEdge(size: number, gap: number): number {
  // Insetting an equilateral triangle by t on every edge shortens its edge by
  // 2*sqrt(3)*t; half the gap per side gives a full gap between neighbours.
  return Math.max(size - SQRT3 * gap, size * 0.05);
}

export const triangleShape: ShapeDefinition = {
  id: 'triangle',
  name: 'Triangle',
  description: 'Equilateral triangles, alternating point-up and point-down.',
  phase: 2,
  gridHint: 'A column is one triangle; odd columns point down.',

  outline(size: number, gap: number): Vec2[] {
    const edge = insetEdge(size, gap);
    // Circumradius of an equilateral triangle, so the centroid is the origin.
    const radius = edge / SQRT3;
    return [
      { x: -edge / 2, y: -radius / 2 },
      { x: edge / 2, y: -radius / 2 },
      { x: 0, y: radius },
    ];
  },

  layout(column: number, row: number, size: number): Vec2 {
    const height = (size * SQRT3) / 2;
    const pointsUp = column % 2 === 0;
    return {
      x: ((column + 1) * size) / 2,
      // An up triangle's centroid is a third of the way up its row; a down
      // triangle's is two thirds, because its apex is at the bottom.
      y: row * height + (pointsUp ? height / 3 : (height * 2) / 3),
    };
  },

  orientation(column: number): number {
    return column % 2 === 0 ? 0 : 180;
  },

  metrics(grid: WallGrid, size: number, gap: number): WallMetrics {
    const bounds = wallBoundsFor(triangleShape, grid, size, gap);
    return {
      columns: grid.columns,
      rows: grid.rows,
      width: bounds.width,
      height: bounds.height,
      // Two columns make one full triangle width; a row is one triangle high.
      pitchX: size,
      pitchY: (size * SQRT3) / 2,
      panelSize: size,
    };
  },
};
